/**
 * TimelineEmailItem - Item `kind: 'email'` da Timeline V2 do ticket.
 *
 * Construído na Spec A Task 8 SEM dados reais ainda — o backend só emite
 * `kind: 'activity'` até o Plano B plugar a fonte `ticket-emails` (ver
 * comentário em `timeline-service.ts`). O shape (`TicketTimelineItem` com
 * `direction`/`emailHtml`/`emailMeta`) já está no `@troia-v3/types` 1.2.139,
 * então este componente renderiza corretamente assim que o backend passar a
 * devolver itens desse `kind` — nada aqui precisa mudar no Plano B.
 *
 * - **Inbound** (`direction: 'inbound'`): balão claro, header De/Para(+Cc),
 *   assunto, corpo via `EmailHtmlFrame` (iframe sandboxed — HTML externo é
 *   adversarial/quebrado, não passa por allowlist). Fallback texto puro se
 *   `emailHtml` não vier.
 * - **Outbound** (`direction: 'outbound'`): balão roxo claro (mesma
 *   `--theme-primary-background` usada em badges/notas do módulo), header
 *   Para(+Cc), assunto, corpo via `SafeHtml` (HTML já é nosso, gerado pelo
 *   mesmo pipeline do editor rico/`sanitize-html`) + badge de status de
 *   entrega (`emailMeta.deliveryStatus` — enum ainda não fechado pelo Plano
 *   B; mapeamento abaixo é defensivo/best-effort, com fallback pro texto cru).
 *
 * Anexos e legado `contentFormat: 'text'` seguem a mesma regra do resto da
 * timeline (chips/thumbnails via `TimelineAttachments`; texto puro nunca
 * interpretado como HTML).
 */
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { TicketTimelineItem } from '@troia-v3/types';
import { EmailHtmlFrame } from '@/shared/components/ui/EmailHtmlFrame';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { SafeHtml } from './SafeHtml';
import { TimelineAttachments } from './TimelineActivityItem';

export interface TimelineEmailItemProps {
  item: TicketTimelineItem;
}

interface DeliveryStatusVisual {
  icon: IconName;
  label: string;
  color: string;
}

const DELIVERY_STATUS_VISUAL: Record<string, DeliveryStatusVisual> = {
  sent: { icon: 'send', label: 'Enviado', color: '#3B82F6' },
  delivered: { icon: 'check-circle', label: 'Entregue', color: '#10B981' },
  read: { icon: 'check-circle', label: 'Lido', color: '#10B981' },
  opened: { icon: 'eye', label: 'Aberto', color: '#10B981' },
  failed: { icon: 'x-circle', label: 'Falhou', color: '#EF4444' },
  bounced: { icon: 'alert-triangle', label: 'Retornou', color: '#EF4444' },
  pending: { icon: 'clock', label: 'Pendente', color: '#F59E0B' },
  queued: { icon: 'clock', label: 'Na fila', color: '#F59E0B' },
};

function getDeliveryStatusVisual(status: string): DeliveryStatusVisual {
  return DELIVERY_STATUS_VISUAL[status.toLowerCase()] ?? { icon: 'mail', label: status, color: '#6B7280' };
}

function formatTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ptBR });
  } catch {
    return 'Data inválida';
  }
}

export const TimelineEmailItem: React.FC<TimelineEmailItemProps> = ({ item }) => {
  const isInbound = item.direction !== 'outbound';
  const emailMeta = item.emailMeta;
  const attachments = item.attachments ?? [];
  const testIdPrefix = `ticket-timeline-email-${item.id}`;
  const statusVisual = emailMeta?.deliveryStatus ? getDeliveryStatusVisual(emailMeta.deliveryStatus) : undefined;

  const plainTextBody = (
    <div className="text-sm whitespace-pre-wrap" style={{ color: 'var(--theme-card-text)' }}>
      {item.content || 'Sem conteúdo disponível'}
    </div>
  );

  const renderBody = () => {
    // Inbound NUNCA passa pelo SafeHtml (fix pós-review M2): HTML externo é
    // adversarial e só renderiza dentro do iframe sandboxed. Sem `emailHtml`,
    // fallback é SEMPRE texto puro — mesmo que `contentFormat` diga 'html',
    // não confiamos em allowlist pra conteúdo de terceiros.
    if (isInbound) {
      if (item.emailHtml) {
        return (
          <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            <EmailHtmlFrame html={item.emailHtml} />
          </div>
        );
      }
      return plainTextBody;
    }
    // Outbound: HTML é nosso (mesmo pipeline do editor rico/sanitize-html)
    if (item.contentFormat === 'html' && item.content) {
      return <SafeHtml html={item.content} data-testid={`${testIdPrefix}-html`} />;
    }
    return plainTextBody;
  };

  return (
    <div
      data-testid={testIdPrefix}
      className="rounded-lg p-4"
      style={{
        backgroundColor: isInbound ? 'var(--theme-card-background)' : 'var(--theme-primary-background)',
        border: `1px solid ${isInbound ? 'var(--theme-card-border)' : 'rgba(139, 92, 246, 0.3)'}`,
        borderLeft: '3px solid #3B82F6',
      }}
    >
      {/* Header: direção + timestamp */}
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1 font-bold flex-shrink-0"
          style={{ height: 18, padding: '0 7px', borderRadius: 5, fontSize: 10, color: '#3B82F6', backgroundColor: 'rgba(59, 130, 246, 0.14)' }}
        >
          <Icon name={isInbound ? 'inbox' : 'send'} className="w-3 h-3" />
          {isInbound ? 'Recebido' : 'Enviado'}
        </span>
        <span className="text-xs ml-auto flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
          {formatTime(item.createdAt)}
        </span>
      </div>

      {/* De/Para/Cc */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
        {isInbound && (
          <span>
            <strong style={{ color: 'var(--theme-card-text)' }}>De:</strong> {emailMeta?.from ?? 'Desconhecido'}
          </span>
        )}
        {emailMeta?.to && emailMeta.to.length > 0 && (
          <span>
            <strong style={{ color: 'var(--theme-card-text)' }}>Para:</strong> {emailMeta.to.join(', ')}
          </span>
        )}
        {emailMeta?.cc && emailMeta.cc.length > 0 && (
          <span>
            <strong style={{ color: 'var(--theme-card-text)' }}>Cc:</strong> {emailMeta.cc.join(', ')}
          </span>
        )}
      </div>

      {emailMeta?.subject && (
        <div className="text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>
          {emailMeta.subject}
        </div>
      )}

      {/* Corpo */}
      {renderBody()}

      {/* Status de entrega (outbound) */}
      {statusVisual && !isInbound && (
        <div className="flex items-center gap-1.5 mt-2 text-xs" data-testid={`${testIdPrefix}-delivery-status`}>
          <Icon name={statusVisual.icon} className="w-3.5 h-3.5" style={{ color: statusVisual.color }} />
          <span style={{ color: statusVisual.color }}>{statusVisual.label}</span>
        </div>
      )}

      {/* Anexos */}
      <TimelineAttachments attachments={attachments} testIdPrefix={testIdPrefix} />
    </div>
  );
};

TimelineEmailItem.displayName = 'TimelineEmailItem';
